/**
 * Popup transport — the three calls the widget makes for M6 popups.
 *
 * Deliberately dumb: fetch config, fire an event, submit a form. Deciding
 * *whether* to show a popup lives in `@prochat/shared` (pure) and
 * `./popup-frequency.ts` (storage); this file only speaks HTTP.
 *
 * All calls are credentialed (`credentials: 'include'`) like
 * `/v1/widget/session`, and the API does the same origin allowlist check
 * the session endpoint does — an `appKey` is public, so it is not a secret.
 */

import type { PopupPublicDto } from '@prochat/shared'

export type PopupEventName = 'impression' | 'close' | 'submit'

export interface PopupPageContext {
  url: string
  referrer?: string
  utmSource?: string
  utmMedium?: string
  utmCampaign?: string
  userAgent?: string
  device?: string
}

export interface PopupTransportOptions {
  apiBaseUrl: string
  fetchImpl?: typeof fetch
}

export interface PopupSubmitResult {
  ok: boolean
  conversationId?: string
  contactId?: string
  error?: string
}

function base(opts: PopupTransportOptions): string {
  return opts.apiBaseUrl.endsWith('/') ? opts.apiBaseUrl.slice(0, -1) : opts.apiBaseUrl
}

function fetcher(opts: PopupTransportOptions): typeof fetch {
  return opts.fetchImpl ?? globalThis.fetch.bind(globalThis)
}

/**
 * Active popups for this app. The server filters `status = 'active'` and
 * the allowed origin; the remaining conditions are evaluated client-side
 * because only the browser holds the referrer, device and scroll position.
 */
export async function fetchPopupConfig(args: {
  apiBaseUrl: string
  appKey: string
  url: string
  anonymousId: string
  fetchImpl?: typeof fetch
  signal?: AbortSignal
}): Promise<PopupPublicDto[]> {
  const query = new URLSearchParams({ appKey: args.appKey, url: args.url })
  const res = await fetcher(args)(`${base(args)}/v1/widget/popups?${query.toString()}`, {
    method: 'GET',
    credentials: 'include',
    headers: { Accept: 'application/json', 'X-ProChat-Anonymous-Id': args.anonymousId },
    signal: args.signal,
  })
  if (!res.ok) throw new Error(`popup config ${res.status}`)
  const json = (await res.json()) as { popups?: PopupPublicDto[] }
  return Array.isArray(json.popups) ? json.popups : []
}

/**
 * Fire-and-forget impression/close tracking. A dropped analytics call is
 * never worth surfacing to a visitor, so this resolves even on failure —
 * callers can `await` it for ordering without a try/catch.
 */
export async function trackPopupEvent(args: {
  apiBaseUrl: string
  appKey: string
  popupId: string
  event: PopupEventName
  anonymousId: string
  page?: PopupPageContext
  payload?: Record<string, unknown>
  fetchImpl?: typeof fetch
}): Promise<void> {
  try {
    await fetcher(args)(`${base(args)}/v1/widget/popups/${args.popupId}/events`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        appKey: args.appKey,
        anonymousId: args.anonymousId,
        event: args.event,
        page: args.page,
        payload: args.payload ?? {},
      }),
    })
  } catch {
    // Best-effort.
  }
}

/**
 * Last-chance delivery while the tab is closing. `sendBeacon` survives
 * unload when `fetch` does not, which is exactly the case that matters for
 * a `close` event fired by a close button or the × button.
 */
export function trackPopupEventBeacon(args: {
  apiBaseUrl: string
  appKey: string
  popupId: string
  event: PopupEventName
  anonymousId: string
  payload?: Record<string, unknown>
}): boolean {
  const beacon = globalThis.navigator?.sendBeacon
  if (typeof beacon !== 'function') return false
  const body = new Blob(
    [
      JSON.stringify({
        appKey: args.appKey,
        anonymousId: args.anonymousId,
        event: args.event,
        payload: args.payload ?? {},
      }),
    ],
    { type: 'application/json' },
  )
  try {
    return beacon.call(
      globalThis.navigator,
      `${base(args)}/v1/widget/popups/${args.popupId}/events`,
      body,
    )
  } catch {
    return false
  }
}

/**
 * Submit the captured form. This is the call that creates the lead — the
 * response carries the conversation the widget may then open.
 */
export async function submitPopup(args: {
  apiBaseUrl: string
  appKey: string
  popupId: string
  anonymousId: string
  values: Record<string, string>
  page?: PopupPageContext
  fetchImpl?: typeof fetch
}): Promise<PopupSubmitResult> {
  try {
    const res = await fetcher(args)(`${base(args)}/v1/widget/popups/${args.popupId}/submit`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        appKey: args.appKey,
        anonymousId: args.anonymousId,
        values: args.values,
        page: args.page,
      }),
    })
    if (!res.ok) {
      const text = await safeReadError(res)
      return { ok: false, error: text }
    }
    const json = (await res.json()) as {
      conversationId?: string
      contactId?: string
    }
    return {
      ok: true,
      conversationId: json.conversationId,
      contactId: json.contactId,
    }
  } catch (err: unknown) {
    return { ok: false, error: err instanceof Error ? err.message : 'network error' }
  }
}

async function safeReadError(res: Response): Promise<string> {
  try {
    return (await res.text()).slice(0, 200)
  } catch {
    return res.statusText
  }
}
